// authService.js
import {
    getAuth,
    setPersistence,
    browserLocalPersistence,
    signInWithEmailAndPassword,
    getMultiFactorResolver,
    PhoneAuthProvider,
    PhoneMultiFactorGenerator,
    RecaptchaVerifier,
    signOut,
    multiFactor,
    sendPasswordResetEmail
} from 'firebase/auth';
import firebaseConfigService from './firebaseConfig';

export class AuthService {
    constructor() {
        this.firebaseAuth = getAuth(firebaseConfigService.getServices().app);
        this.persistenceReady = this.initializePersistence();

        // Disable app verification (reCAPTCHA) when using emulators to avoid CAPTCHA_CHECK_FAILED
        if (firebaseConfigService.isUsingEmulators()) {
            this.firebaseAuth.settings.appVerificationDisabledForTesting = true;
        }

        this.recaptchaVerifier = null;
        this.baseURL = process.env.REACT_APP_API_URL;
        this.mfaVerificationId = null;
        this.tokenRefreshIntervalId = null;
        this.idTokenUnsubscribe = null;
        this.tokenRefreshFailureCount = 0;
        this.lastTokenRefreshWarningAt = 0;
    }

    async initializePersistence() {
        try {
            await setPersistence(this.firebaseAuth, browserLocalPersistence);
        } catch (error) {
            console.error('Failed to set Firebase auth persistence:', error);
        }
    }

    async ensurePersistenceReady() {
        if (this.persistenceReady) {
            await this.persistenceReady;
        }
    }

    async forgotPassword(email) {
        await this.ensurePersistenceReady();
        await sendPasswordResetEmail(this.firebaseAuth, email);
    }

    async logout() {
        await this.ensurePersistenceReady();
        await signOut(this.firebaseAuth);
        localStorage.removeItem('googleIdToken');
        localStorage.removeItem('currentUser');
        window.dispatchEvent(new CustomEvent('authStatusChanged', {
            detail: { authenticated: false, user: null }
        }));
    }

    initRecaptcha(elementId) {
        if (!document.getElementById(elementId)) return;

        // Always clear existing to prevent "already rendered" DOM errors
        if (this.recaptchaVerifier) {
            this.recaptchaVerifier.clear();
        }

        this.recaptchaVerifier = new RecaptchaVerifier(this.firebaseAuth, elementId, {
            size: 'invisible',
            callback: (_response) => {
                // reCAPTCHA solved - verification will proceed automatically
                console.log('reCAPTCHA solved');
            }
        });
        return this.recaptchaVerifier;
    }

    /**
     * Enroll MFA for the current user
     * @param {Object} user - Firebase user object
     * @param {string} phoneNumber - Phone number in E.164 format (e.g., +1234567890)
     * @param {string} recaptchaContainerId - DOM element ID for reCAPTCHA container
     * @returns {Promise<string>} - Verification ID for completing enrollment
     */
    async startMFAEnrollment(user, phoneNumber, recaptchaContainerId) {
        // Initialize invisible reCAPTCHA
        if (this.recaptchaVerifier) {
            this.recaptchaVerifier.clear();
        }
        this.recaptchaVerifier = new RecaptchaVerifier(this.firebaseAuth, recaptchaContainerId, {
            size: 'invisible'
        });

        // Get MFA session
        const multiFactorSession = await multiFactor(user).getSession();

        // Specify phone number and MFA session
        const phoneInfoOptions = {
            phoneNumber: phoneNumber,
            session: multiFactorSession
        };

        const phoneAuthProvider = new PhoneAuthProvider(this.firebaseAuth);

        // Send SMS verification code
        this.mfaVerificationId = await phoneAuthProvider.verifyPhoneNumber(
            phoneInfoOptions,
            this.recaptchaVerifier
        );

        return this.mfaVerificationId;
    }

    /**
     * Complete MFA enrollment with verification code
     * @param {Object} user - Firebase user object
     * @param {string} verificationCode - SMS verification code entered by user
     * @param {string} displayName - Display name for this MFA factor (e.g., "Personal Phone")
     * @returns {Promise<void>}
     */
    async completeMFAEnrollment(user, verificationCode, displayName = 'Phone') {
        if (!this.mfaVerificationId) {
            throw new Error('MFA enrollment not started. Call startMFAEnrollment first.');
        }

        const cred = PhoneAuthProvider.credential(this.mfaVerificationId, verificationCode);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        // Complete enrollment
        await multiFactor(user).enroll(multiFactorAssertion, displayName);

        // Clear the verification ID after successful enrollment
        this.mfaVerificationId = null;
    }

    async loginWithEmail(email, password) {
        await this.ensurePersistenceReady();
        try {
            const userCredential = await signInWithEmailAndPassword(this.firebaseAuth, email, password);
            return await this.handleFirebaseUser(userCredential.user);
        } catch (error) {
            if (error.code === 'auth/multi-factor-auth-required') {
                return {
                    mfaRequired: true,
                    resolver: getMultiFactorResolver(this.firebaseAuth, error)
                };
            }
            throw error;
        }
    }

    async sendMFACode(resolver, selectedIndex = 0) {
        // Verify the selected hint is a phone factor
        const selectedHint = resolver.hints[selectedIndex];
        if (selectedHint.factorId !== PhoneMultiFactorGenerator.FACTOR_ID) {
            throw new Error('Selected factor is not a phone factor.');
        }

        const phoneAuthProvider = new PhoneAuthProvider(this.firebaseAuth);

        if (!this.recaptchaVerifier) {
            throw new Error('reCAPTCHA not initialized.');
        }

        // Build phone info options with the hint and session
        const phoneInfoOptions = {
            multiFactorHint: selectedHint,
            session: resolver.session
        };

        // Send SMS verification code
        return await phoneAuthProvider.verifyPhoneNumber(phoneInfoOptions, this.recaptchaVerifier);
    }

    async verifyMFACode(resolver, verificationId, code) {
        const cred = PhoneAuthProvider.credential(verificationId, code);
        const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);

        // Complete sign-in with the second factor
        const userCredential = await resolver.resolveSignIn(multiFactorAssertion);
        return await this.handleFirebaseUser(userCredential.user);
    }

    /**
     * Wait for Firebase Auth to initialize and restore session
     * @returns {Promise<Object|null>} Firebase user or null
     */
    waitForAuthReady() {
        return new Promise((resolve) => {
            const unsubscribe = this.firebaseAuth.onAuthStateChanged((user) => {
                unsubscribe();
                resolve(user);
            });
        });
    }

    async getValidIdToken(options = {}) {
        await this.ensurePersistenceReady();
        const { forceRefresh = false, minValiditySeconds = 300 } = options;
        const firebaseUser = this.firebaseAuth.currentUser;
        if (!firebaseUser) return null;

        try {
            if (forceRefresh) {
                const refreshed = await firebaseUser.getIdToken(true);
                localStorage.setItem('googleIdToken', refreshed);
                return refreshed;
            }

            const tokenResult = await firebaseUser.getIdTokenResult();
            const expiresAtMs = new Date(tokenResult.expirationTime).getTime();
            const nowMs = Date.now();
            const needsRefresh = expiresAtMs <= (nowMs + (minValiditySeconds * 1000));

            const idToken = needsRefresh
                ? await firebaseUser.getIdToken(true)
                : tokenResult.token;

            localStorage.setItem('googleIdToken', idToken);
            this.tokenRefreshFailureCount = 0;
            return idToken;
        } catch (error) {
            console.error('Failed to get valid Firebase ID token:', error);
            this.tokenRefreshFailureCount += 1;

            const now = Date.now();
            const warningCooldownMs = 5 * 60 * 1000; // 5 minutes
            if (this.tokenRefreshFailureCount >= 3 && (now - this.lastTokenRefreshWarningAt) > warningCooldownMs) {
                this.lastTokenRefreshWarningAt = now;
                window.dispatchEvent(new CustomEvent('authTokenRefreshWarning', {
                    detail: {
                        failureCount: this.tokenRefreshFailureCount,
                        message: 'Connection/auth refresh issue detected. You may need to reconnect if this continues.'
                    }
                }));
            }
            return null;
        }
    }

    /**
     * Check authentication status on app start/refresh
     * Rehydrates session from localStorage and verifies token
     */
    async checkAuthStatus() {
        try {
            await this.ensurePersistenceReady();
            // 1. Wait for Firebase to attempt session restoration
            const firebaseUser = await this.waitForAuthReady();

            if (!firebaseUser) {
                // No session restored by Firebase
                localStorage.removeItem('googleIdToken');
                localStorage.removeItem('currentUser');
                return null;
            }

            // 2. Get a valid token (refresh only if close to expiry)
            const idToken = await this.getValidIdToken({ minValiditySeconds: 300 });
            if (!idToken) {
                await this.logout();
                return null;
            }

            // 3. Verify with backend
            let response;
            try {
                response = await fetch(`${this.baseURL}/api/auth/verify-firebase`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ idToken })
                });
            } catch (networkError) {
                const cachedUser = localStorage.getItem('currentUser');
                if (cachedUser) {
                    return { user: JSON.parse(cachedUser) };
                }
                throw networkError;
            }

            if (!response.ok) {
                console.warn('Token verification failed during auth check');
                await this.logout();
                return null;
            }

            const data = await response.json();
            const user = data.data.user;

            // 4. Update stored user/token to match the fresh session
            localStorage.setItem('googleIdToken', idToken);
            localStorage.setItem('currentUser', JSON.stringify(user));

            return { user };
        } catch (error) {
            console.error('Error checking auth status:', error);
            const cachedUser = localStorage.getItem('currentUser');
            if (cachedUser) {
                try {
                    return { user: JSON.parse(cachedUser) };
                } catch {
                    // fall through to logout
                }
            }
            await this.logout();
            return null;
        }
    }

    async handleFirebaseUser(_firebaseUser) {
        await this.ensurePersistenceReady();
        const idToken = await this.getValidIdToken({ forceRefresh: true });
        if (!idToken) {
            throw new Error('Unable to retrieve Firebase ID token');
        }
        const response = await fetch(`${this.baseURL}/api/auth/verify-firebase`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ idToken })
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || 'Backend Verification Failed');

        localStorage.setItem('googleIdToken', idToken);
        localStorage.setItem('currentUser', JSON.stringify(data.data.user));

        window.dispatchEvent(new CustomEvent('authStatusChanged', {
            detail: {
                authenticated: true,
                user: data.data.user
            }
        }));
        return data.data.user;
    }

    async sendSessionWarning() {
        try {
            const token = localStorage.getItem('googleIdToken');
            if (!token) return;

            await fetch(`${this.baseURL}/api/auth/session-warning-sms`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                }
            });
        } catch (error) {
            console.error('Failed to send session warning SMS', error);
        }
    }

    /**
     * Setup listener for Firebase ID token changes (refresh)
     * Keeps localStorage in sync with Firebase SDK's auto-refresh
     */
    setupTokenRefreshListener() {
        if (this.idTokenUnsubscribe) return this.idTokenUnsubscribe;

        console.log('Setting up token refresh listener');
        this.idTokenUnsubscribe = this.firebaseAuth.onIdTokenChanged(async (user) => {
            if (user) {
                try {
                    const idToken = await this.getValidIdToken({ minValiditySeconds: 300 });
                    const currentToken = localStorage.getItem('googleIdToken');
                    if (idToken && idToken !== currentToken) {
                        console.log('Token refreshed by Firebase SDK, updating localStorage');
                        localStorage.setItem('googleIdToken', idToken);
                    }
                } catch (error) {
                    console.error('Error getting refreshed token:', error);
                }
            } else {
                localStorage.removeItem('googleIdToken');
            }
        });

        // Background heartbeat refresh for long-lived mobile sessions.
        if (!this.tokenRefreshIntervalId) {
            this.tokenRefreshIntervalId = setInterval(() => {
                this.getValidIdToken({ minValiditySeconds: 600 }).catch(() => { });
            }, 10 * 60 * 1000);
        }

        return this.idTokenUnsubscribe;
    }
}

export const authService = new AuthService();
